import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { completeLocalSignup, fetchAccountLegalDocuments, fetchSignupState, verifySignupEmail, type LegalDocumentsDescriptor } from "@/api/auth";
import { ApiError } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { AuthShell } from "@/components/layout/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PASSWORD_DENYLIST_ERROR_MESSAGE, PASSWORD_LENGTH_ERROR_MESSAGE, PASSWORD_LENGTH_HINT, isPasswordLengthValid } from "./password-policy";

const inFlightEmailVerifications = new Map<string, Promise<void>>();
function verifySignupEmailOnce(token: string) {
  const existing = inFlightEmailVerifications.get(token);
  if (existing) return existing;
  const pending = verifySignupEmail(token);
  inFlightEmailVerifications.set(token, pending);
  const clear = () => { if (inFlightEmailVerifications.get(token) === pending) inFlightEmailVerifications.delete(token); };
  void pending.then(clear, clear);
  return pending;
}

type Field = "full-name" | "terms" | "password" | "password-confirmation";
type Phase = "verifying" | "ready" | "expired" | "completed" | "checking";

export function SignupVerifyPage() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const started = useRef(false);
  const submitting = useRef(false);
  const [terms, setTerms] = useState<LegalDocumentsDescriptor | null>(null);
  const [phase, setPhase] = useState<Phase>("verifying");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState<{ field: Field; message: string } | null>(null);
  const [waitSeconds, setWaitSeconds] = useState(0);

  useEffect(() => {
    if (!busy && fieldError) document.getElementById(`signup-${fieldError.field}`)?.focus();
  }, [busy, fieldError]);
  useEffect(() => {
    if (waitSeconds <= 0) return;
    const timer = setTimeout(() => setWaitSeconds((seconds) => Math.max(0, seconds - 1)), 1_000);
    return () => clearTimeout(timer);
  }, [waitSeconds]);

  const handleFailure = async (failure: unknown) => {
    const code = failure instanceof ApiError ? failure.code : "";
    if (code === "signup_completed" || code === "signup_session_unavailable") { setPhase("completed"); return; }
    if (failure instanceof ApiError && failure.status === 401) { setPhase("expired"); return; }
    if (code === "legal_documents_changed") {
      setAccepted(false);
      try { setTerms(await fetchAccountLegalDocuments()); }
      catch { setError("約款を読み込めませんでした。時間をおいて登録状態を確認してください。"); setPhase("checking"); return; }
      setFieldError({ field: "terms", message: "約款が更新されました。内容を確認して、もう一度同意してください。" });
      return;
    }
    const messages: Record<string, { field: Field; message: string }> = {
      password_length_invalid: { field: "password", message: PASSWORD_LENGTH_ERROR_MESSAGE },
      password_utf8_length_invalid: { field: "password", message: "パスワードのデータ量が上限を超えています。短くして入力してください。" },
      password_denylisted: { field: "password", message: PASSWORD_DENYLIST_ERROR_MESSAGE },
      signup_full_name_invalid: { field: "full-name", message: "氏名を1〜200文字で入力してください。" },
    };
    if (messages[code]) { setFieldError(messages[code]); return; }
    if (code === "validation_failed" && failure instanceof ApiError) {
      const diagnostic = failure.details?.diagnostic as { validationIssues?: { path?: string; instancePath?: string }[] } | undefined;
      const path = diagnostic?.validationIssues?.[0]?.path ?? diagnostic?.validationIssues?.[0]?.instancePath ?? "";
      const field: Field = path.includes("password") ? "password" : (path.includes("Version") || path.includes("legalDocumentsAccepted")) ? "terms" : "full-name";
      setFieldError({ field, message: field === "password" ? PASSWORD_LENGTH_ERROR_MESSAGE : field === "terms" ? "約款を確認して同意してください。" : "氏名を1〜200文字で入力してください。" });
      return;
    }
    if (failure instanceof ApiError && failure.status === 429) {
      setWaitSeconds(failure.retryAfterSeconds ?? 60);
      setError("操作が続いています。表示の時間を待ってから、もう一度お試しください。");
      return;
    }
    if (code === "signup_unavailable") { setError("登録を保存できませんでした。時間をおいて、パスワードを入力し直してお試しください。"); return; }
    if (code === "signup_conflict") { setError("登録を完了できませんでした。登録済みの場合はログインしてください。"); return; }
    setError(code === "signup_in_progress" ? "登録処理中です。少し待ってから登録状態を確認してください。" : "登録結果を確認できませんでした。少し待ってから登録状態を確認してください。");
    setPhase("checking");
  };

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = new URLSearchParams(globalThis.location.hash.slice(1)).get("token");
    globalThis.history.replaceState(null, "", globalThis.location.pathname);
    void (async () => {
      try {
        if (token) await verifySignupEmailOnce(token);
        else await fetchSignupState();
        setTerms(await fetchAccountLegalDocuments());
        setPhase("ready");
      } catch (failure) { setPhase("checking"); await handleFailure(failure); }
    })();
  }, []);

  const checkState = async () => {
    setBusy(true);
    setError("");
    try {
      await fetchSignupState();
      setTerms(await fetchAccountLegalDocuments());
      setPhase("ready");
    } catch (failure) { await handleFailure(failure); }
    finally { setBusy(false); }
  };

  const completeLocal = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting.current || waitSeconds > 0) return;
    setError("");
    setFieldError(null);
    if (!fullName.trim() || [...fullName.trim()].length > 200) { setFieldError({ field: "full-name", message: "氏名を1〜200文字で入力してください。" }); return; }
    if (!accepted || !terms) { setFieldError({ field: "terms", message: "約款・ポリシーを確認して同意してください。" }); return; }
    if (!isPasswordLengthValid(password)) { setFieldError({ field: "password", message: PASSWORD_LENGTH_ERROR_MESSAGE }); return; }
    if (password !== passwordConfirmation) { setFieldError({ field: "password-confirmation", message: "確認用パスワードが一致しません。" }); return; }
    submitting.current = true;
    setBusy(true);
    let committed = false;
    try {
      await completeLocalSignup({ fullName: fullName.trim(), password, legalDocumentsAccepted: true, termsAndPoliciesVersion: terms.termsAndPolicies.version, serviceTermsVersion: terms.serviceTerms.version, cookiePolicyVersion: terms.cookiePolicy.version });
      committed = true;
      await refreshMe();
      navigate("/auth/complete#result=success", { replace: true });
    } catch (failure) {
      if (committed) setPhase("completed");
      else await handleFailure(failure);
    } finally {
      setPassword("");
      setPasswordConfirmation("");
      setBusy(false);
      submitting.current = false;
    }
  };
  const fieldMessage = (field: Field) => fieldError?.field === field
    ? <p id={`signup-${field}-error`} role="alert" className="text-sm text-destructive">{fieldError.message}</p> : null;
  const fieldAttributes = (field: Field) => ({ "aria-invalid": fieldError?.field === field, "aria-describedby": fieldError?.field === field ? `signup-${field}-error` : undefined });

  if (phase === "verifying") return <AuthShell title="メールを確認しています" description="しばらくお待ちください。"><p role="status">確認中…</p></AuthShell>;
  if (phase === "expired") return <AuthShell title="確認リンクを利用できません" description="リンクの期限が切れているか、確認情報を取得できません。"><Link className="text-primary underline" to="/signup">確認メールを再送する</Link></AuthShell>;
  if (phase === "completed") return <AuthShell title="アカウント登録は完了しています" description="ログインしてEXTONE Driveをご利用ください。"><Link className="text-primary underline" to="/login">ログインする</Link></AuthShell>;
  if (phase === "checking") return <AuthShell title="登録状態の確認" description={error || "登録状態を確認してください。"}><Button onClick={() => void checkState()} disabled={busy}>登録状態を確認する</Button></AuthShell>;
  return <AuthShell title="アカウントを設定" description="氏名とパスワードを入力し、約款を確認してください。">
    <form className="space-y-4" onSubmit={completeLocal} noValidate>
      {error ? <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}{error.includes("ログイン") ? <Link className="ml-2 underline" to="/login">ログインする</Link> : null}</div> : null}
      <div className="space-y-2"><Label htmlFor="signup-full-name">氏名</Label><Input id="signup-full-name" value={fullName} onChange={(event) => setFullName(event.target.value)} disabled={busy} autoComplete="name" {...fieldAttributes("full-name")} />{fieldMessage("full-name")}</div>
      <div><label className="flex items-start gap-3 rounded-md border p-3 text-sm"><input id="signup-terms" type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} disabled={busy} className="mt-1" {...fieldAttributes("terms")} /><span><a href={terms!.termsAndPolicies.url} target="_blank" rel="noreferrer" className="text-primary underline">約款・ポリシー一覧</a>を確認し、<a href={terms!.serviceTerms.url} target="_blank" rel="noreferrer" className="text-primary underline">利用約款</a>と<a href={terms!.cookiePolicy.url} target="_blank" rel="noreferrer" className="text-primary underline">Cookieポリシー</a>に同意します。</span></label>{fieldMessage("terms")}</div>
      <div className="space-y-2"><Label htmlFor="signup-password">パスワード</Label><Input id="signup-password" type="password" value={password} onChange={(event) => setPassword(event.target.value)} disabled={busy} autoComplete="new-password" {...fieldAttributes("password")} /><p className="text-xs text-muted-foreground">{PASSWORD_LENGTH_HINT}</p>{fieldMessage("password")}</div>
      <div className="space-y-2"><Label htmlFor="signup-password-confirmation">パスワード（確認）</Label><Input id="signup-password-confirmation" type="password" value={passwordConfirmation} onChange={(event) => setPasswordConfirmation(event.target.value)} disabled={busy} autoComplete="new-password" {...fieldAttributes("password-confirmation")} />{fieldMessage("password-confirmation")}</div>
      {waitSeconds > 0 ? <p role="status">再送まであと{waitSeconds}秒</p> : null}
      <Button type="submit" className="w-full" disabled={busy || waitSeconds > 0}>パスワードで登録</Button>
    </form>
  </AuthShell>;
}
